import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  LayoutDashboard,
  FileText,
  Users,
  LogOut,
  Mail,
  Palette,
  Eye,
  Save,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Check,
  Menu,
  X,
  Code,
  Wand2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Template type labels
const templateTypes = {
  nova_denuncia: {
    pt: "Nova Denúncia (Admin)",
    en: "New Report (Admin)",
    icon: "🔔"
  },
  nova_ouvidoria: {
    pt: "Nova Ouvidoria (Admin)",
    en: "New Ombudsman (Admin)",
    icon: "📨"
  },
  status_atualizado: {
    pt: "Status Atualizado",
    en: "Status Updated",
    icon: "🔄"
  },
  investigador_atribuido: {
    pt: "Investigador Atribuído",
    en: "Investigator Assigned",
    icon: "👤"
  },
  confirmacao_denuncia: {
    pt: "Confirmação de Denúncia",
    en: "Report Confirmation",
    icon: "✅"
  }
};

export default function EmailTemplatesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'pt';
  const { user, empresa, logout, loading: authLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Email config state
  const [emailConfig, setEmailConfig] = useState({
    logo_url: "",
    cor_primaria: "#1a1a2e",
    cor_secundaria: "#f5a623",
    cor_texto: "#1a1a2e",
    cor_fundo: "#f8fafc",
    texto_rodape: "",
    assinatura: ""
  });
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editedTemplate, setEditedTemplate] = useState({ assunto: "", conteudo_html: "" });
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [expandedVars, setExpandedVars] = useState(false);

  // Redirect if not logged in
  useEffect(() => {
    // Wait for auth loading to complete before checking
    if (authLoading) return;
    
    const token = localStorage.getItem("token");
    if (!token || !user) {
      navigate(`/${slug}/login`);
    }
  }, [slug, navigate, authLoading, user]);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    if (user) {
      fetchData();
    }
  }, [user, slug, authLoading]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch email config
      const configRes = await axios.get(`${API_URL}/empresas/${slug}/email-config`, { headers });
      setEmailConfig({
        logo_url: configRes.data.logo_url || "",
        cor_primaria: configRes.data.cor_primaria || "#1a1a2e",
        cor_secundaria: configRes.data.cor_secundaria || "#f5a623",
        cor_texto: configRes.data.cor_texto || "#1a1a2e",
        cor_fundo: configRes.data.cor_fundo || "#f8fafc",
        texto_rodape: configRes.data.texto_rodape || "",
        assinatura: configRes.data.assinatura || ""
      });
      
      // Fetch templates
      const templatesRes = await axios.get(`${API_URL}/empresas/${slug}/email-templates`, { headers });
      setTemplates(templatesRes.data.templates || []);
      setVariables(templatesRes.data.variables || {});
      
      // Select first template
      if (templatesRes.data.templates?.length > 0) {
        const first = templatesRes.data.templates[0];
        setSelectedTemplate(first);
        setEditedTemplate({ assunto: first.assunto, conteudo_html: first.conteudo_html });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(currentLang === 'en' ? 'Error loading data' : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const saveEmailConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/empresas/${slug}/email-config`,
        emailConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(currentLang === 'en' ? 'Configuration saved!' : 'Configuração salva!');
    } catch (error) {
      toast.error(currentLang === 'en' ? 'Error saving' : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/empresas/${slug}/email-templates/${selectedTemplate.tipo}`,
        editedTemplate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setTemplates(prev => prev.map(t => 
        t.tipo === selectedTemplate.tipo 
          ? { ...t, ...editedTemplate, is_custom: true }
          : t
      ));
      setSelectedTemplate(prev => ({ ...prev, ...editedTemplate, is_custom: true }));
      
      toast.success(currentLang === 'en' ? 'Template saved!' : 'Template salvo!');
    } catch (error) {
      toast.error(currentLang === 'en' ? 'Error saving' : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!selectedTemplate?.is_custom) return;
    
    if (!window.confirm(currentLang === 'en' 
      ? 'Reset template to default? This will delete your customizations.'
      : 'Redefinir template para o padrão? Isso excluirá suas customizações.'
    )) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API_URL}/empresas/${slug}/email-templates/${selectedTemplate.tipo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh templates
      await fetchData();
      toast.success(currentLang === 'en' ? 'Template reset!' : 'Template redefinido!');
    } catch (error) {
      toast.error(currentLang === 'en' ? 'Error resetting' : 'Erro ao redefinir');
    } finally {
      setSaving(false);
    }
  };

  const previewTemplate = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/empresas/${slug}/email-templates/preview`,
        editedTemplate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewHtml(res.data.conteudo_html);
      setShowPreview(true);
    } catch (error) {
      toast.error(currentLang === 'en' ? 'Error generating preview' : 'Erro ao gerar preview');
    } finally {
      setSaving(false);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditedTemplate({ assunto: template.assunto, conteudo_html: template.conteudo_html });
    setShowPreview(false);
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('template-content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editedTemplate.conteudo_html;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setEditedTemplate(prev => ({ ...prev, conteudo_html: newText }));
    } else {
      setEditedTemplate(prev => ({ 
        ...prev, 
        conteudo_html: prev.conteudo_html + variable 
      }));
    }
  };

  const content = {
    pt: {
      title: "Configurar Emails",
      subtitle: "Personalize os emails enviados pela plataforma",
      configTab: "Configuração",
      templatesTab: "Templates",
      appearance: "Aparência",
      colors: "Cores",
      primaryColor: "Cor Primária",
      secondaryColor: "Cor Secundária",
      textColor: "Cor do Texto",
      bgColor: "Cor de Fundo",
      customization: "Personalização",
      footerText: "Texto do Rodapé",
      footerPlaceholder: "Ex: Este é um email automático...",
      signature: "Assinatura",
      signaturePlaceholder: "Ex: Atenciosamente, Equipe de Compliance",
      save: "Salvar Configuração",
      templateEditor: "Editor de Template",
      subject: "Assunto do Email",
      htmlContent: "Conteúdo HTML",
      variables: "Variáveis Disponíveis",
      commonVars: "Variáveis Comuns",
      preview: "Visualizar",
      saveTemplate: "Salvar Template",
      resetTemplate: "Redefinir para Padrão",
      customized: "Customizado",
      default: "Padrão",
      clickToInsert: "Clique para inserir",
      menu: {
        dashboard: "Dashboard",
        cases: "Casos",
        investigators: "Investigadores",
        emails: "Emails",
        logout: "Sair"
      }
    },
    en: {
      title: "Configure Emails",
      subtitle: "Customize emails sent by the platform",
      configTab: "Configuration",
      templatesTab: "Templates",
      appearance: "Appearance",
      colors: "Colors",
      primaryColor: "Primary Color",
      secondaryColor: "Secondary Color",
      textColor: "Text Color",
      bgColor: "Background Color",
      customization: "Customization",
      footerText: "Footer Text",
      footerPlaceholder: "e.g. This is an automated email...",
      signature: "Signature",
      signaturePlaceholder: "e.g. Best regards, Compliance Team",
      save: "Save Configuration",
      templateEditor: "Template Editor",
      subject: "Email Subject",
      htmlContent: "HTML Content",
      variables: "Available Variables",
      commonVars: "Common Variables",
      preview: "Preview",
      saveTemplate: "Save Template",
      resetTemplate: "Reset to Default",
      customized: "Customized",
      default: "Default",
      clickToInsert: "Click to insert",
      menu: {
        dashboard: "Dashboard",
        cases: "Cases",
        investigators: "Investigators",
        emails: "Emails",
        logout: "Logout"
      }
    }
  };

  const c = content[currentLang];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 bg-white border-r border-slate-200
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            {empresa?.logo_url ? (
              <img src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)} alt="" className="w-10 h-10 rounded-lg object-contain" />
            ) : (
              <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h2 className="font-semibold text-[#1a1a2e] truncate">{empresa?.nome}</h2>
              <p className="text-xs text-slate-500">Compliance</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          <Link to={`/${slug}/admin`} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50">
            <LayoutDashboard className="w-5 h-5" />
            {c.menu.dashboard}
          </Link>
          <Link to={`/${slug}/admin/casos`} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50">
            <FileText className="w-5 h-5" />
            {c.menu.cases}
          </Link>
          <Link to={`/${slug}/admin/investigadores`} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50">
            <Users className="w-5 h-5" />
            {c.menu.investigators}
          </Link>
          <Link to={`/${slug}/admin/emails`} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#1a1a2e]/5 text-[#1a1a2e] font-medium">
            <Mail className="w-5 h-5" />
            {c.menu.emails}
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-[#1a1a2e] rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.nome?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1a1a2e] truncate">{user?.nome}</p>
              <p className="text-xs text-slate-500">Admin</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 w-full"
          >
            <LogOut className="w-4 h-4" />
            {c.menu.logout}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-4 lg:p-8 lg:ml-0 mt-16 lg:mt-0">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[#1a1a2e]">{c.title}</h1>
            <p className="text-slate-600">{c.subtitle}</p>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="config" className="space-y-6">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="config" className="data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">
                <Palette className="w-4 h-4 mr-2" />
                {c.configTab}
              </TabsTrigger>
              <TabsTrigger value="templates" className="data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">
                <Code className="w-4 h-4 mr-2" />
                {c.templatesTab}
              </TabsTrigger>
            </TabsList>

            {/* Configuration Tab */}
            <TabsContent value="config">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-[#1a1a2e] mb-6 flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  {c.appearance}
                </h3>

                {/* Colors */}
                <div className="mb-8">
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">{c.colors}</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">{c.primaryColor}</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={emailConfig.cor_primaria}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, cor_primaria: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={emailConfig.cor_primaria}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, cor_primaria: e.target.value }))}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{c.secondaryColor}</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={emailConfig.cor_secundaria}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, cor_secundaria: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={emailConfig.cor_secundaria}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, cor_secundaria: e.target.value }))}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{c.textColor}</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={emailConfig.cor_texto}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, cor_texto: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={emailConfig.cor_texto}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, cor_texto: e.target.value }))}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{c.bgColor}</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={emailConfig.cor_fundo}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, cor_fundo: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={emailConfig.cor_fundo}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, cor_fundo: e.target.value }))}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customization */}
                <div className="mb-8">
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">{c.customization}</Label>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-slate-500">{c.footerText}</Label>
                      <Input
                        value={emailConfig.texto_rodape}
                        onChange={(e) => setEmailConfig(prev => ({ ...prev, texto_rodape: e.target.value }))}
                        placeholder={c.footerPlaceholder}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{c.signature}</Label>
                      <Textarea
                        value={emailConfig.assinatura}
                        onChange={(e) => setEmailConfig(prev => ({ ...prev, assinatura: e.target.value }))}
                        placeholder={c.signaturePlaceholder}
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                <Button onClick={saveEmailConfig} disabled={saving} className="bg-[#1a1a2e]">
                  <Save className="w-4 h-4 mr-2" />
                  {c.save}
                </Button>
              </div>
            </TabsContent>

            {/* Templates Tab */}
            <TabsContent value="templates">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Template List */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Templates</h3>
                  <div className="space-y-2">
                    {templates.map((template) => (
                      <button
                        key={template.tipo}
                        onClick={() => selectTemplate(template)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedTemplate?.tipo === template.tipo
                            ? 'border-[#1a1a2e] bg-[#1a1a2e]/5'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{templateTypes[template.tipo]?.icon}</span>
                          <div className="flex-1">
                            <p className="font-medium text-sm text-[#1a1a2e]">
                              {templateTypes[template.tipo]?.[currentLang]}
                            </p>
                            <p className={`text-xs ${template.is_custom ? 'text-[#f5a623]' : 'text-slate-400'}`}>
                              {template.is_custom ? c.customized : c.default}
                            </p>
                          </div>
                          {template.is_custom && (
                            <Wand2 className="w-4 h-4 text-[#f5a623]" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Template Editor */}
                <div className="lg:col-span-2 space-y-4">
                  {selectedTemplate && (
                    <>
                      <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <h3 className="font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
                          <Code className="w-5 h-5" />
                          {c.templateEditor}
                        </h3>

                        <div className="space-y-4">
                          <div>
                            <Label>{c.subject}</Label>
                            <Input
                              value={editedTemplate.assunto}
                              onChange={(e) => setEditedTemplate(prev => ({ ...prev, assunto: e.target.value }))}
                              className="mt-1 font-mono text-sm"
                            />
                          </div>

                          <div>
                            <Label>{c.htmlContent}</Label>
                            <Textarea
                              id="template-content"
                              value={editedTemplate.conteudo_html}
                              onChange={(e) => setEditedTemplate(prev => ({ ...prev, conteudo_html: e.target.value }))}
                              className="mt-1 font-mono text-xs"
                              rows={15}
                            />
                          </div>

                          {/* Variables */}
                          <div className="border border-slate-200 rounded-lg">
                            <button
                              onClick={() => setExpandedVars(!expandedVars)}
                              className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <span>{c.variables}</span>
                              {expandedVars ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            {expandedVars && (
                              <div className="p-4 border-t border-slate-200 bg-slate-50">
                                <p className="text-xs text-slate-500 mb-3">{c.clickToInsert}</p>
                                
                                {/* Template-specific variables */}
                                {variables[selectedTemplate.tipo] && (
                                  <div className="mb-4">
                                    <p className="text-xs font-medium text-slate-600 mb-2">
                                      {templateTypes[selectedTemplate.tipo]?.[currentLang]}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {variables[selectedTemplate.tipo].map(([varName, desc]) => (
                                        <button
                                          key={varName}
                                          onClick={() => insertVariable(varName)}
                                          className="px-2 py-1 bg-white border border-slate-200 rounded text-xs font-mono hover:bg-[#1a1a2e] hover:text-white hover:border-[#1a1a2e] transition-colors"
                                          title={desc}
                                        >
                                          {varName}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Common variables */}
                                {variables.common && (
                                  <div>
                                    <p className="text-xs font-medium text-slate-600 mb-2">{c.commonVars}</p>
                                    <div className="flex flex-wrap gap-2">
                                      {variables.common.map(([varName, desc]) => (
                                        <button
                                          key={varName}
                                          onClick={() => insertVariable(varName)}
                                          className="px-2 py-1 bg-white border border-slate-200 rounded text-xs font-mono hover:bg-[#f5a623] hover:text-[#1a1a2e] hover:border-[#f5a623] transition-colors"
                                          title={desc}
                                        >
                                          {varName}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3 mt-6">
                          <Button onClick={previewTemplate} disabled={saving} variant="outline">
                            <Eye className="w-4 h-4 mr-2" />
                            {c.preview}
                          </Button>
                          <Button onClick={saveTemplate} disabled={saving} className="bg-[#1a1a2e]">
                            <Save className="w-4 h-4 mr-2" />
                            {c.saveTemplate}
                          </Button>
                          {selectedTemplate.is_custom && (
                            <Button onClick={resetTemplate} disabled={saving} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                              <RotateCcw className="w-4 h-4 mr-2" />
                              {c.resetTemplate}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Preview */}
                      {showPreview && (
                        <div className="bg-white rounded-xl border border-slate-200 p-6">
                          <h3 className="font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
                            <Eye className="w-5 h-5" />
                            {c.preview}
                          </h3>
                          <div 
                            className="border border-slate-200 rounded-lg p-4 bg-white"
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
